// src/tiles/ScoreBadge.jsx
import React from 'react';
import { Chip, Box } from '@mui/material';

// ✅ Pull qualitative labels from one source
import {
  describeJtbdImportance,
  describeJtbdSatisfaction,
  describeReasonNotChosenImportance,
  describeInsightSentimentIntensity,
  describePositiveImportance,
  describeDrawbackImportance
} from '../../utils/qualitative';

// map each metric to a fill color from theme (track stays neutral)
const fillColorByMetric = {
  importance: 'primary.main',
  satisfaction: 'success.main',
  reason_not_chosen_importance: 'primary.main',
  intensity: 'secondary.main',
  // newly wired:
  positive_importance: 'primary.main',
  drawback_importance: 'primary.main'
};

function Bar10({ value, metricKey }) {
  const v = Math.max(0, Math.min(10, Number(value) || 0));
  const pct = (v / 10) * 100;
  const fillColor = fillColorByMetric[metricKey] || 'text.primary';

  return (
    <Box
      aria-label={`Score ${v} out of 10`}
      sx={{
        display: 'inline-block',
        width: 64,
        height: 6,
        borderRadius: 4,
        bgcolor: 'action.disabledBackground',
        overflow: 'hidden',
        ml: 0.75,
        verticalAlign: 'middle'
      }}
    >
      <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: fillColor }} />
    </Box>
  );
}

// Centralized mapper → calls into qualitative.js
function describeByMetric(metricKey, numeric) {
  switch (metricKey) {
    case 'importance':
      return describeJtbdImportance(numeric);
    case 'satisfaction':
      return describeJtbdSatisfaction(numeric);
    case 'reason_not_chosen_importance':
      return describeReasonNotChosenImportance(numeric);
    case 'intensity':
      return describeInsightSentimentIntensity(numeric);
    // used by ImpressionTile (positive/negative buckets)
    case 'positive_importance':
      return describePositiveImportance(numeric);
    case 'drawback_importance':
      return describeDrawbackImportance(numeric);
    default:
      return '';
  }
}

export default function ScoreBadge({ label, value = 0, metricKey = 'text' }) {
  const numeric = Number.isFinite(Number(value)) ? Number(value) : 0;
  const shown = Math.round(numeric * 10) / 10;

  const qual = describeByMetric(metricKey, numeric);
  const text = qual ? `${shown} · ${qual}` : String(value);

  const showBar = [
    'importance',
    'satisfaction',
    'reason_not_chosen_importance',
    'intensity',
    'positive_importance',
    'drawback_importance'
  ].includes(metricKey);

  return (
    <Chip
      size="small"
      label={
        <span>
          <strong>{label}:</strong> {text}
          {showBar && <Bar10 value={numeric} metricKey={metricKey} />}
        </span>
      }
    />
  );
}
